import { Suspense, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { CareProviderSearch } from "@/modules/care-provider/simple-search/care-provider-search"
import { ChangeCareProviderReConcludeDialog } from "@/modules/patients/detail/progress-note/components/change-care-provider-re-conclude-dialog"
import {
  ChangeCareProviderResult,
  useChangeCareProviderFlow,
} from "@/modules/patients/detail/progress-note/hooks/useChangeCareProviderFlow"
import { getCareProviderSearchFilter } from "@/modules/patients/detail/progress-note/lib/get-care-provider-search-type"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useReConcludePendingStore } from "@/modules/patients/detail/store/useReConcludePendingStore"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  progressNote: ProgressNoteData
  careProviderId: string
}

const EditCareProviderDialog = ({ open, onOpenChange, progressNote, careProviderId }: Props) => {
  const { t } = useTranslation()
  const [selectedCareProviderId, setSelectedCareProviderId] = useState<string | null>(careProviderId)
  const [isSaving, setIsSaving] = useState(false)
  const [isReConcludeOpen, setIsReConcludeOpen] = useState(false)
  const { searchType, specialtyId } = getCareProviderSearchFilter(progressNote.pointOfCare)
  const pendingResult = useReConcludePendingStore((state) => state.pendingByProgressNote.get(progressNote.id) ?? null)
  const setPending = useReConcludePendingStore((state) => state.setPending)
  const clearPending = useReConcludePendingStore((state) => state.clearPending)
  const reConcludeChangeResult: ChangeCareProviderResult = pendingResult ?? { carePlanPreview: null }

  const { changeCareProvider } = useChangeCareProviderFlow({ progressNote })

  const handleClose = () => {
    setSelectedCareProviderId(careProviderId)
    setIsReConcludeOpen(false)
    onOpenChange(false)
  }

  const handleSave = () => {
    if (selectedCareProviderId === careProviderId) return
    setIsSaving(true)
    changeCareProvider({
      newCareProviderId: selectedCareProviderId,
      onSuccess: (result) => {
        setIsSaving(false)
        setPending(progressNote.id, result)
      },
      onError: () => {
        setIsSaving(false)
      },
    })
  }

  const isUnchanged = selectedCareProviderId === careProviderId
  const canSave = !!selectedCareProviderId && !isUnchanged && !isSaving
  const canReConclude = !!pendingResult && isUnchanged

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentCustom
        onOpenAutoFocus={(e) => {
          e.preventDefault()
        }}
        data-testid="edit-care-provider-dialog"
        className="flex w-full max-w-150 min-w-0 flex-col gap-0 p-0">
        <DialogHeader className="h-16 shrink-0 pt-4 pr-4 pb-2 pl-6">
          <DialogTitle className="px-0 py-0" data-testid="edit-care-provider-dialog-title">
            {t("patient.conclude.editCareProviderAndReConclude")}
          </DialogTitle>
        </DialogHeader>
        <div className="w-full px-6 py-4">
          <Suspense fallback={<p className="h-12" />}>
            <CareProviderSearch
              label={t("episodeOfCare.progressNote.pointOfCare.careProvider")}
              searchType={searchType}
              specialtyId={specialtyId}
              name="edit-care-provider"
              value={selectedCareProviderId}
              allowUnknownCareProvider
              defaultAdvancedSearchFilters={{ specialtyId }}
              analyticsSectionName="edit_care_provider"
              onSelect={(id) => setSelectedCareProviderId(id)}
              onRemove={() => setSelectedCareProviderId(null)}
            />
          </Suspense>
        </div>
        <DialogFooter className="h-20 shrink-0 items-center" data-testid="edit-care-provider-dialog-footer">
          <Button type="button" variant="secondary" onClick={handleClose}>
            {t("common.button.cancel")}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!canSave}
            isLoading={isSaving}
            onClick={handleSave}
            data-testid="edit-care-provider-save-button">
            {t("common.button.save")}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!canReConclude}
            onClick={() => {
              FaroInsightsWrapper.pushEvent("check_out_conclude")
              setIsReConcludeOpen(true)
            }}
            data-testid="edit-care-provider-re-conclude-button">
            {t("patient.conclude.reConclude")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
      {isReConcludeOpen && (
        <ChangeCareProviderReConcludeDialog
          open
          onOpenChange={(o) => {
            if (!o) setIsReConcludeOpen(false)
          }}
          progressNote={progressNote}
          changeResult={reConcludeChangeResult}
          onReConcluded={() => clearPending(progressNote.id)}
        />
      )}
    </Dialog>
  )
}

export { EditCareProviderDialog }
